import React, { Dispatch, MutableRefObject, ReactNode, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';
import {
  AvailableLineData,
  PerformanceMetricsCashflow,
  PerformanceMetricsProfitAndLoss,
  FinancialDatasetRequest,
  LiveReportDetail,
  ReportTemplateDetail,
  FinancialConfig,
  RowFormula,
  AvailableColumnCalculations,
  ForecastListItem,
  ForecastComparisonRequest,
  ForecastComparisonResponse,
  ReportingTemplateFormula,
  ImageResponse,
  PerformanceMetricsBalanceSheet,
  ReportTemplateRequest
} from '@futrli-sdk/predict';
import { ReportScheduleItem, DispatchedReportItem } from '@futrli-sdk/report_scheduling';
import { createContext } from '../core/context';
import { LiveReportPayload } from '../core/api/reporting/report-builder';
import useReports from './use-reports';
import {
  AxisOptions,
  CalendarOptions,
  DataField,
  FinData,
  FinFieldOptions,
  LayoutData,
  Margins,
  PageSetup,
  PAGE_NUMBERS_DISPLAY,
  PAGE_SIZES,
  TableStyle,
  TextData,
  WidgetData,
  WIDGET_TYPES,
  RowTypes,
  ImageData,
  ReportMappingData,
  DatasetOptionType,
  ClassifierType,
  MappingData
} from './types';
import { LayoutItem as Layout } from './components/grid-layout';
import { PERIODS } from '../forecast/detail/performance/components/period-dropdown';
import { matchOnboardingEditReportUrl } from '@futrli/shared-services';
import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';
import { TableFiltersWithSort } from '../utils/table-filters/filter-table-types';

interface Props {
  report: LiveReportDetail | ReportTemplateDetail;
  patchReport: (reportId: string, data: LiveReportPayload & Partial<ReportTemplateRequest>) => Promise<void>;
  organisationId?: string;
  workspaceId?: string;
  isTemplate?: boolean;
  children: ReactNode;
  isMasterPage?: boolean;
  viewPage?: boolean;
  isDashboardPage?: boolean;
}
interface ReportBuilderContextProps {
  // api data
  availableFinFields: AvailableLineData;
  cashflowMetrics: PerformanceMetricsCashflow | undefined;
  balanceSheetMetrics: PerformanceMetricsBalanceSheet | undefined;
  allFinFields: DataField[];
  allFinFieldsArray: DataField[];
  cashFlowAndBalanceSheetRefs: string[];
  profitAndLossMetrics: PerformanceMetricsProfitAndLoss | undefined;
  financialConfig?: FinancialConfig;
  rowFormulaData: (RowFormula | ReportingTemplateFormula)[] | undefined;
  futrliFormulaData: ReportingTemplateFormula[] | undefined;
  columnCalculations: AvailableColumnCalculations | undefined;
  forecasts: (ForecastListItem | ConsolidatedForecastListItem)[];
  scheduledReports?: ReportScheduleItem[];
  dispatchedReports?: DispatchedReportItem[];

  //report data
  reportTitle: string;
  updateReportTitle: (v: string) => void;
  updateTemplateTags: (v: string[]) => void;
  reportId: string;
  reportData: {
    pageLayout: Layout[];
    pageLayoutData: LayoutData[];
    pageSetup: PageSetup;
    templateIdPendingInsert?: string;
  };

  // page setup
  pageSetup: PageSetup;
  setPageSetup: Dispatch<SetStateAction<PageSetup>>;
  setPageSize: (v: PAGE_SIZES) => void;
  setPageOrientation: (v: 'portrait' | 'landscape' | 'dashboard') => void;
  setBackgroundColor: (v: string) => void;
  setMargins: (v: Margins[]) => void;
  setShowMargins: (v: boolean) => void;
  setZoom: (v: number) => void;
  setReportPageNumbersDisplay: (pageNum: PAGE_NUMBERS_DISPLAY) => void;

  // fields
  getSelectedDatasets: () => string[];
  addSelectedDataset: (v: string) => void;
  setSelectedDatasets: (v: string[]) => void;
  removeSelectedDataset: (ref: string) => void;
  getSelectedFinFields: () => DataField[];
  addSelectedFinField: (v: DataField) => void;
  addSelectFinFieldOrDataset: (
    selectedItem: {
      type: DatasetOptionType;
      ref: string;
      name: string;
      classifier?: ClassifierType;
    },
    targetType: DatasetOptionType,
    isSecondaryAxis: boolean
  ) => void;
  removeSelectedFinField: (ref: string) => void;
  setSelectedFinFields: (
    v: DataField[],
    excludedChildren?: Record<string, string[]>,
    includedChildren?: Record<string, string[]>
  ) => void;
  getSelectedCalFields: () => DataField[];
  setSelectedCalFields: (v: DataField[]) => void;
  removeSelectedCalField: (ref: string) => void;
  getRowFormulas: () => void;
  addSelectedVarianceField: (v: DataField) => void;
  removeSelectedVarianceField: (ref: string) => void;
  getSelectedVarianceFields: () => DataField[];
  setSelectedVariances: (v: DataField[]) => void;
  getSelectedComparisons: (ref: string) => string[][];
  setSelectedComparisons: (ref: string, v: string[][]) => void;
  setAppWidgetSelectedPeriod: (p: PERIODS) => void;
  setWidgetFiltersAndSort: (filtersAndSort: TableFiltersWithSort) => void;

  // layout and data
  pageLayout: Layout[];
  setPageLayout: Dispatch<SetStateAction<Layout[]>>;
  pageLayoutData: LayoutData[];
  setPageLayoutData: Dispatch<SetStateAction<LayoutData[]>>;

  // widget data
  currentCardId: string;
  setCurrentCardId: Dispatch<SetStateAction<string>>;
  currentCard: LayoutData | undefined;
  updateWidgetData: (v: WidgetData, page?: number) => void;
  setCalendarOptions: (v: CalendarOptions | undefined) => void;
  setPrimaryAxisOptions: (v: AxisOptions | undefined) => void;
  setSecondaryAxisOptions: (v: AxisOptions | undefined) => void;
  setFinFieldOptions: (ref: string, v: FinFieldOptions) => void;
  setFinFieldOptionsAllFields: (v: FinFieldOptions, isSecondary?: boolean, excludedClassifiers?: string[]) => void;
  getFinFieldsOptions: () => { ref: string; options: FinFieldOptions | undefined }[];
  setMultipleFinFieldOptions: (fieldsOptions: { ref: string; options: FinFieldOptions }[]) => void;
  removeFinFieldOptions: (ref: string) => void;
  removeRowFormula: (ref: string) => void;
  updateFormulaNameInReport: (ref: string, name: string) => void;
  setWidgetType: (v: WIDGET_TYPES) => void;
  setWidgetTitle: (s: string) => void;
  setWidgetSubTitle: (s: string) => void;
  setRowsStyle: (customRowStyle: { type: RowTypes; style: TableStyle }[]) => void;
  setWidgetDataByKey: (key: string, value: unknown) => void;
  setCollapsedFields: (fields: string[]) => void;

  // text and image panel data
  updateTextData: (v: TextData, page?: number) => void;
  updateSelectedImage: (img: ImageData) => void;
  uploadedImages: ImageResponse[];
  setUploadedImages: Dispatch<SetStateAction<ImageResponse[]>>;

  // widget Fin Data
  updateFinDataForWidget: (i: string, query: FinancialDatasetRequest[]) => void;
  emptyFinDataForWidget: (i: string) => void;
  layoutFinData: Record<string, Record<string, FinData> | undefined>; // cardId -> forecastId -> FinData
  setLayoutFinData: (data: Record<string, Record<string, FinData> | undefined>) => void;
  getFinDataForLiveReport: () => void;

  // widget Comparison Data
  updateComparisonDataForWidget: (i: string, query: ForecastComparisonRequest) => void;
  emptyComparisonDataForWidget: (i: string) => void;
  layoutComparisonData: Record<string, ForecastComparisonResponse | undefined>;
  setLayoutComparisonData: Dispatch<SetStateAction<Record<string, ForecastComparisonResponse | undefined>>>;

  // widget data getter
  getWidgetType: () => WIDGET_TYPES | undefined;
  getWidgetTitle: () => string | undefined;
  getWidgetSubTitle: () => string | undefined;
  getPageWidth: () => number;
  getPageHeight: () => number;

  allRequestsFinished: boolean;

  // fetch metrics data
  fetchCashflowMetrics: () => void;
  fetchProfitAndLossMetrics: () => void;
  fetchBalanceSheetMetrics: () => void;

  // other
  workAreaWidth: number;
  setWorkAreaWidth: Dispatch<SetStateAction<number>>;
  isAutoSaving: boolean;
  setIsAutoSaving: Dispatch<SetStateAction<boolean>>;
  isEditingTitle: boolean;
  setIsEditingTitle: Dispatch<SetStateAction<boolean>>;
  reportColors: string[];
  isReady: boolean;
  selectedFinFieldsIncChildren: DataField[];
  headerFooterLimits: MutableRefObject<number[][]>;
  heightAdjusted: MutableRefObject<boolean>;
  pageBreaks: number;
  setPageBreaks: Dispatch<SetStateAction<number>>;
  getDatasetName: (i: string) => string;
  isPendingNewFinData: boolean;
  setIsPendingNewFinData: Dispatch<SetStateAction<boolean>>;
  isOnboarding: boolean;
  isTemplate: boolean;
  isViewPage: boolean;
  isWorkspaceTemplate: boolean;
  isPublished: boolean;
  dataReferenceMapping: ReportMappingData | undefined;
  setDataReferenceMapping: Dispatch<SetStateAction<ReportMappingData | undefined>>;
  mapDatasetsModalOpen: boolean;
  setMapDatasetsModalOpen: Dispatch<SetStateAction<boolean>>;
  mapTemplateDataModalOpen: boolean;
  setMapTemplateDataModalOpen: Dispatch<SetStateAction<boolean>>;
  datasetsToMap: string[];
  setDatasetsToMap: Dispatch<SetStateAction<string[]>>;
  dataRefsToMap: string[];
  setDataRefsToMap: Dispatch<SetStateAction<string[]>>;
  reportSourceTemplateId: string | undefined;
  userTimezone: string | undefined;
  revalidateScheduleReportData: () => Promise<void>;
  tags: string[];
  reportDsToMap: MappingData[];
  reportFieldsToMap: MappingData[];
  isMasterPage: boolean;
  itemToBringIntoView: string | null;
  setItemToBringIntoView: (id: string | null) => void;
  scrollReportTo?: [number, number];
  setScrollReportTo: (coords: [number, number]) => void;
  widgetsAwaitingData: string[];
  locale: string;
  setLocale: (newLocale: string) => void;
  updateReportLocale: (newLocale: string) => void;
}

const ReportBuilderContext = createContext<ReportBuilderContextProps>();

function useContextSetup(
  report: LiveReportDetail | ReportTemplateDetail,
  patchReport: (reportId: string, data: LiveReportPayload & Partial<ReportTemplateRequest>) => Promise<void>,
  organisationId?: string,
  workspaceId?: string,
  isTemplate?: boolean,
  isMasterPage?: boolean,
  viewPage?: boolean,
  isDashboardPage?: boolean
) {
  const { organisationId: organisationIdParams, workspaceId: workspaceIdParams } = useParams();
  const isOnboarding = !!matchOnboardingEditReportUrl(location.pathname);
  const isViewPage = !!viewPage;

  const contextData = {
    ...useReports({
      report,
      patchReport,
      organisationId: organisationIdParams || organisationId || '',
      workspaceId: workspaceIdParams || workspaceId || '',
      isTemplate,
      isMasterPage,
      isViewPage,
      isDashboardPage
    }),
    isOnboarding,
    isViewPage
  };
  return contextData;
}

export function ReportBuilderContextProvider({
  report,
  children,
  patchReport,
  organisationId,
  workspaceId,
  isTemplate,
  isMasterPage,
  viewPage,
  isDashboardPage
}: Props) {
  const value = useContextSetup(
    report,
    patchReport,
    organisationId,
    workspaceId,
    isTemplate,
    isMasterPage,
    viewPage,
    isDashboardPage
  );

  return <ReportBuilderContext.Provider value={value}>{children}</ReportBuilderContext.Provider>;
}

export const useReportBuilderContext = ReportBuilderContext.useContext;
